// ========================================================
// FAST HIT Website — Header / Navigation
// Dual-mode header: Public Marketing vs Authenticated App
// Shows all mobile app options when logged in
// ========================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  Download,
  LogIn,
  User,
  Search,
  Bell,
  LogOut,
  Settings,
  ChevronDown,
  Activity,
  Shield,
  Trophy,
  Home,
  Radio,
  Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AnnouncementStrip } from '../landing/AnnouncementStrip';
import './Header.css';

const PUBLIC_NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/live-scores', label: 'Live Scores' },
  { to: '/players', label: 'Players' },
  { to: '/teams', label: 'Teams' },
  { to: '/tournaments', label: 'Tournaments' },
  { to: '/community', label: 'Community' },
];

const APP_NAV_LINKS = [
  { to: '/app', label: 'Home' },
  { to: '/app/matches', label: 'Matches' },
  { to: '/app/teams', label: 'Teams' },
  { to: '/app/tournaments', label: 'Tournaments' },
  { to: '/app/my-cricket', label: 'My Cricket' },
  { to: '/community', label: 'Community' },
  { to: '/live-scores', label: 'Live Scores' },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, profile, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isAppRoute = location.pathname.startsWith('/app');
  const showAppNav = isAuthenticated || isAppRoute;
  const activeNavLinks = showAppNav ? APP_NAV_LINKS : PUBLIC_NAV_LINKS;

  // Close menus on navigation
  useEffect(() => {
    setMobileOpen(false);
    setUserDropdownOpen(false);
  }, [location.pathname]);

  // Track scroll for sticky header backdrop
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (mobileOpen) {
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
      if (userDropdownOpen) {
        setUserDropdownOpen(false);
      }
    }
  }, [mobileOpen, userDropdownOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        mobileOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target as Node)
      ) {
        setMobileOpen(false);
      }
      if (
        userDropdownOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileOpen, userDropdownOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.warn('Logout error:', err);
    }
  };

  const displayName =
    profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Cricketer';
  const userInitials = displayName.slice(0, 2).toUpperCase();

  return (
    <header
      className={`header ${scrolled ? 'header--scrolled' : ''}`}
      role="banner"
    >
      {/* Show marketing announcement only on public pages, not inside /app */}
      {!isAppRoute && <AnnouncementStrip />}

      <div className="header__bar">
        <div className="header__container">
          {/* Logo */}
          <Link
            to={showAppNav ? '/app' : '/'}
            className="header__logo"
            aria-label="FAST HIT Home"
          >
            <img src="/logo.png" alt="FAST HIT" className="header__logo-img" />
            <span className="header__logo-text">FAST HIT</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="header__nav hide-mobile" aria-label="Main navigation">
            <ul className="header__nav-list">
              {activeNavLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === '/app' || link.to === '/'}
                    className={({ isActive }) =>
                      `header__nav-link ${isActive ? 'header__nav-link--active' : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Actions */}
          <div className="header__actions hide-mobile">
            <Link
              to="/search"
              className="btn btn--outline btn--sm btn--pill"
              aria-label="Search Cricket"
            >
              <Search size={14} />
              <span>Search</span>
            </Link>

            {isAuthenticated ? (
              <>
                <Link
                  to="/app/notifications"
                  className="header__action-icon-btn"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <Bell size={18} />
                </Link>

                {/* User Dropdown */}
                <div className="header__user-menu-wrap" ref={userMenuRef}>
                  <button
                    className="header__user-avatar-btn"
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    aria-expanded={userDropdownOpen}
                    aria-label="User account menu"
                  >
                    <span className="header__user-avatar">{userInitials}</span>
                    <span className="header__user-name">{displayName}</span>
                    <ChevronDown size={14} className="text-muted" />
                  </button>

                  {userDropdownOpen && (
                    <div className="header__user-dropdown card">
                      <div className="header__user-dropdown-info">
                        <span className="font-semibold text-text block">{displayName}</span>
                        <span className="text-xs text-muted block truncate">
                          {user?.email || profile?.role || 'FAST HIT Member'}
                        </span>
                      </div>
                      <div className="header__user-dropdown-links">
                        <Link
                          to="/app/profile"
                          className="header__user-dropdown-item"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <User size={15} />
                          <span>My Profile</span>
                        </Link>
                        <Link
                          to="/app/my-cricket"
                          className="header__user-dropdown-item"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <Activity size={15} />
                          <span>My Cricket Stats</span>
                        </Link>
                        <Link
                          to="/app/teams"
                          className="header__user-dropdown-item"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <Shield size={15} />
                          <span>My Teams</span>
                        </Link>
                        <Link
                          to="/app/tournaments"
                          className="header__user-dropdown-item"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <Trophy size={15} />
                          <span>Tournaments Hub</span>
                        </Link>
                        <Link
                          to="/app/settings"
                          className="header__user-dropdown-item"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <Settings size={15} />
                          <span>Account Settings</span>
                        </Link>
                        {(profile?.roles?.includes('admin') || profile?.roles?.includes('superAdmin')) && (
                          <a
                            href="/admin"
                            className="header__user-dropdown-item text-primary font-semibold"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <Shield size={15} />
                            <span>Admin Console ↗</span>
                          </a>
                        )}
                        <button
                          onClick={handleLogout}
                          className="header__user-dropdown-item header__user-dropdown-item--danger"
                        >
                          <LogOut size={15} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/download" className="btn btn--outline btn--sm btn--pill">
                  <Download size={14} />
                  <span>Download App</span>
                </Link>
                <Link to="/login" className="btn btn--primary btn--sm btn--pill">
                  <LogIn size={14} />
                  <span>Sign In</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            ref={toggleRef}
            className="header__mobile-toggle hide-desktop"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="header__mobile-overlay" aria-hidden="true" />
      )}

      {/* Mobile Navigation Drawer */}
      <div
        ref={menuRef}
        id="mobile-menu"
        className={`header__mobile-menu hide-desktop ${
          mobileOpen ? 'header__mobile-menu--open' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {isAuthenticated && (
          <div className="header__mobile-user-card">
            <div className="header__user-avatar">{userInitials}</div>
            <div>
              <span className="font-semibold text-text block text-sm">{displayName}</span>
              <span className="text-xs text-muted block truncate">{user?.email}</span>
            </div>
          </div>
        )}

        <nav aria-label="Mobile navigation">
          <ul className="header__mobile-list">
            {activeNavLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.to === '/app' || link.to === '/'}
                  className={({ isActive }) =>
                    `header__mobile-link ${
                      isActive ? 'header__mobile-link--active' : ''
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="header__mobile-actions">
          {isAuthenticated ? (
            <>
              {(profile?.roles?.includes('admin') || profile?.roles?.includes('superAdmin')) && (
                <a
                  href="/admin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary w-full"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
                >
                  <Shield size={16} />
                  <span>Admin Console ↗</span>
                </a>
              )}
              <Link to="/app/profile" className="btn btn--outline w-full">
                <User size={16} />
                <span>My Profile</span>
              </Link>
              <Link to="/app/settings" className="btn btn--outline w-full">
                <Settings size={16} />
                <span>Settings</span>
              </Link>
              <button onClick={handleLogout} className="btn btn--danger w-full">
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/download" className="btn btn--outline w-full">
                <Download size={16} />
                Download App
              </Link>
              <Link to="/login" className="btn btn--primary w-full">
                <LogIn size={16} />
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
